import { API_BASE_URL } from '@/lib/constants'
import { isHandingOff } from '@/lib/handoff'
import type { ConfigResponse } from '@/hooks/useConfig'

/**
 * Wrks OAuth login entry helpers.
 *
 * Portal entry mode `authorize` opens the plain app URL, so an unauthenticated landing tries the
 * OAuth flow ONCE per browser session with `prompt=none` (already logged-in + consented users get
 * in silently). Wrks answers `login_required` to that silent attempt whenever its login cookie is
 * missing, even when the Wrks session itself is alive (users who last logged in before Wrks
 * introduced the cookie). So the callback retries ONCE without `prompt` (OAUTH_LOGIN_REQUIRED
 * only): Wrks then restores the cookie and returns at once, or shows its login or consent
 * screen. Any other failure lands on the error page with its buttons.
 * The sessionStorage key prevents loops: it is set before each navigation away (`attempted` for
 * the silent attempt, `retried` for the retry) and by an explicit logout or a final callback
 * error (`suppressed`); it is never cleared by a success. A hand-off to another deployment
 * (`lib/handoff.ts`) also stops the auto-start.
 */
const STORAGE_KEY = 'vixgen.oauth.autoStart'
const AUTO_START_ROUTES = /^\/(my|templates(\/[^/]+)?|community)?\/?$/

type AutoStartMark = 'attempted' | 'retried' | 'suppressed'

function readMark(): AutoStartMark | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY)
    return value === 'attempted' || value === 'retried' || value === 'suppressed' ? value : null
  } catch {
    return 'suppressed' // no storage → never auto-start (would loop)
  }
}

function writeMark(mark: AutoStartMark): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, mark)
  } catch {
    // storage unavailable — auto-start is disabled by readMark() anyway
  }
}

export function markOAuthAutoStartAttempted(): void {
  writeMark('attempted')
}

/** Explicit logout / callback error: never auto-start again in this browser session. */
export function suppressOAuthAutoStart(): void {
  writeMark('suppressed')
}

export function shouldAutoStartOAuth(pathname: string, config: ConfigResponse | undefined): boolean {
  if (isHandingOff()) return false
  if (!config?.oauthLoginEnabled) return false
  if (config.freepassEnabled) return false
  if (!AUTO_START_ROUTES.test(pathname)) return false
  return readMark() === null
}

/**
 * The silent attempt came back with OAUTH_LOGIN_REQUIRED (Wrks `login_required`,
 * `consent_required` or `interaction_required`): retry it once without `prompt`.
 */
export function shouldRetryOAuthInteractively(errorCode: string | null): boolean {
  return errorCode === 'OAUTH_LOGIN_REQUIRED' && readMark() === 'attempted'
}

export function markOAuthRetried(): void {
  writeMark('retried')
}

/** Same-origin in-app path only (mirrors the backend's sanitizeReturnTo). */
export function sanitizeReturnTo(raw: string | null | undefined): string | null {
  if (!raw || raw.length > 512) return null
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null
  if (/[\\@\s]/.test(raw) || /:\/\//.test(raw) || /%2f%2f|%5c|%40/i.test(raw)) return null
  return raw
}

export function buildOAuthStartUrl(
  config: ConfigResponse | undefined,
  options: { prompt?: 'none' | 'login'; returnTo?: string | null } = {}
): string {
  const base = config?.oauthStartUrl ?? `${API_BASE_URL}/auth/oauth/start`
  const query = new URLSearchParams()
  if (options.prompt) query.set('prompt', options.prompt)
  const returnTo = sanitizeReturnTo(options.returnTo)
  if (returnTo && returnTo !== '/') query.set('returnTo', returnTo)
  const qs = query.toString()
  return qs ? `${base}?${qs}` : base
}
